import { FileText, FileSpreadsheet, Image, X, FileSearch, Music, Video, Loader2, CheckCircle2, AlertCircle, Database, CalendarClock } from 'lucide-react';
import { formatFileSize } from '@/services/fileUploadService';

interface FilePreviewProps {
  name: string;
  size: number;
  type?: string;
  previewUrl?: string;
  onRemove?: () => void;
  isExpediente?: boolean;
  processingStage?: string;
  /** Extraction status for background pre-processing */
  extractionStatus?: 'extracting' | 'ready' | 'error' | 'indexing' | 'indexed';
  extractionError?: string;
  pageCount?: number;
  onAnalyzeChronology?: () => void;
}

function getFileIcon(type?: string, isExpediente?: boolean) {
  if (isExpediente) {
    return <FileSearch className="h-5 w-5 text-amber-600 flex-shrink-0" />;
  }
  if (!type) return <FileText className="h-5 w-5 text-[#002B5B] flex-shrink-0" />;

  if (type.startsWith('image/')) {
    return <Image className="h-5 w-5 text-[#002B5B] flex-shrink-0" />;
  }
  if (type.startsWith('audio/')) {
    return <Music className="h-5 w-5 text-purple-600 flex-shrink-0" />;
  }
  if (type.startsWith('video/')) {
    return <Video className="h-5 w-5 text-indigo-600 flex-shrink-0" />;
  }
  if (
    type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    type === 'application/vnd.ms-excel'
  ) {
    return <FileSpreadsheet className="h-5 w-5 text-green-700 flex-shrink-0" />;
  }
  return <FileText className="h-5 w-5 text-[#002B5B] flex-shrink-0" />;
}

export function FilePreview({
  name, size, type, previewUrl, onRemove,
  isExpediente, processingStage,
  extractionStatus, extractionError, pageCount,
  onAnalyzeChronology,
}: FilePreviewProps) {
  const isImage = type?.startsWith('image/');

  const borderClass = extractionStatus === 'error'
    ? 'border-red-300 bg-red-50'
    : isExpediente
      ? 'bg-amber-50 border border-amber-200'
      : 'bg-[#E2F1EF]';

  return (
    <div className={`flex items-center gap-2 rounded-lg px-3 py-2 max-w-xs ${borderClass}`}>
      {/* Status icon or file preview */}
      {extractionStatus === 'extracting' ? (
        <Loader2 className="h-5 w-5 text-[#06D3AA] flex-shrink-0 animate-spin" />
      ) : extractionStatus === 'indexing' ? (
        <Database className="h-5 w-5 text-blue-500 flex-shrink-0 animate-pulse" />
      ) : extractionStatus === 'indexed' ? (
        <Database className="h-5 w-5 text-emerald-500 flex-shrink-0" />
      ) : extractionStatus === 'error' ? (
        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
      ) : extractionStatus === 'ready' ? (
        <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
      ) : isImage && previewUrl ? (
        <img
          src={previewUrl}
          alt={name}
          className="h-10 w-10 rounded object-cover flex-shrink-0"
        />
      ) : (
        getFileIcon(type, isExpediente)
      )}

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#002B5B] truncate">{name}</p>
        <div className="flex items-center gap-1.5">
          <p className="text-xs text-[#002B5B]/60">{formatFileSize(size)}</p>
          {extractionStatus === 'extracting' && (
            <span className="text-[10px] font-medium text-[#06D3AA] animate-pulse">
              Extrayendo texto…
            </span>
          )}
          {extractionStatus === 'indexing' && (
            <span className="text-[10px] font-medium text-blue-500 animate-pulse">
              Indexando en RAG…
            </span>
          )}
          {extractionStatus === 'indexed' && (
            <span className="text-[10px] font-medium text-emerald-600">
              Indexado ✓
            </span>
          )}
          {extractionStatus === 'ready' && pageCount && (
            <span className="text-[10px] font-medium text-emerald-600">
              {pageCount} pág.
            </span>
          )}
          {extractionStatus === 'error' && (
            <span className="text-[10px] font-medium text-red-500 truncate max-w-[120px]" title={extractionError}>
              {extractionError || 'Error'}
            </span>
          )}
          {isExpediente && !extractionStatus && (
            <span className="text-[10px] font-medium bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
              Expediente
            </span>
          )}
        </div>
        {processingStage && (
          <p className="text-xs text-amber-600 animate-pulse mt-0.5">{processingStage}</p>
        )}
      </div>
      {onAnalyzeChronology && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onAnalyzeChronology();
          }}
          className="flex-shrink-0 p-1 hover:bg-amber-100 rounded transition-colors text-amber-600 hover:text-amber-700"
          title="Analizar cronología y plazos"
        >
          <CalendarClock className="h-4 w-4" />
        </button>
      )}
      {onRemove && (
        <button
          onClick={onRemove}
          className="flex-shrink-0 p-1 hover:bg-white/50 rounded transition-colors"
        >
          <X className="h-4 w-4 text-[#002B5B]" />
        </button>
      )}
    </div>
  );
}
